import Chip from "@mui/material/Chip";
import type { ChipProps } from "@mui/material/Chip";
import { Stack, Typography } from "@mui/material";
import { CustomCellDate, CrudLink } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { StreamStatusType } from "@/admin/entities/streams";
import type { StreamRow } from "./types";

const statusChipConfig: Record<
  StreamStatusType,
  { label: string; color: ChipProps["color"] }
> = {
  [StreamStatusType.PendingType]: { label: "Ожидает", color: "warning" },
  [StreamStatusType.StartedType]: { label: "В эфире", color: "primary" },
  [StreamStatusType.FinishedType]: { label: "Завершён", color: "success" },
  [StreamStatusType.CancelledType]: { label: "Отменён", color: "default" },
};

function renderStatus(stream: StreamRow["stream"]) {
  if (!stream) return "—";
  const { label, color } = statusChipConfig[stream.status] ?? {
    label: stream.status,
    color: "default" as const,
  };
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Chip label={label} color={color} size="small" />
      {stream.liveConnected && (
        <Chip label="Онлайн" color="error" size="small" variant="outlined" />
      )}
    </Stack>
  );
}

export const getColumnsConfig = (): ColumnDefinition<StreamRow>[] => [
  { name: "id", label: "ID", options: { display: false, filter: false } },
  {
    name: "preview",
    label: "Превью",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const preview = data as StreamRow["preview"];
        if (!preview) return null;
        return (
          <img
            src={preview.url}
            alt=""
            style={{ maxWidth: 96, borderRadius: 4 }}
          />
        );
      },
    },
  },
  {
    name: "title",
    label: "Название",
    options: {
      customBodyRender: (data: unknown) => {
        const stream = data as StreamRow;
        return (
          <CrudLink id={stream.id} tab="details">
            {stream.title}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "totalViews",
    label: "Просмотры",
    options: {
      // Считается резолвером поверх коллекции View, а не хранится на Content —
      // серверной сортировки по нему нет
      sort: false,
      setCellHeaderProps: () => ({ style: { width: 120 } }),
    },
  },
  {
    name: "platform",
    label: "Платформа",
    options: {
      customBodyRender: (data: unknown) => {
        const platform = data as StreamRow["platform"];
        return platform ? platform.title : "—";
      },
    },
  },
  {
    name: "stream",
    label: "Статус эфира",
    options: {
      sort: false,
      setCellHeaderProps: () => ({ style: { width: 190 } }),
      customBodyRender: (data: unknown) =>
        renderStatus(data as StreamRow["stream"]),
    },
  },
  {
    name: "startAt",
    label: "Начало",
    options: {
      sort: false,
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => {
        const dateValue = value as string | null;
        return dateValue ? <CustomCellDate date={dateValue} /> : "—";
      },
    },
  },
  {
    name: "createdAt",
    label: "Создан",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
  {
    name: "deletedAt",
    label: "Удалён",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => {
        const dateValue = value as string | null;
        return dateValue ? <CustomCellDate date={dateValue} /> : "—";
      },
    },
  },
];

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Эфиры",
  refetchList: "Обновить список",
  deleteItems: "Удалить эфиры",
  itemsDeleted: "Эфиры удалены",
  restoreItems: "Восстановить эфиры",
  itemsRestored: "Эфиры восстановлены",
  cancel: "Отмена",
  editItem: "Эфир",
  showTitle: "Детали",
});

export const getMobileColumnsConfig = (): DataListViewColumns<StreamRow> => ({
  main: {
    title: "Эфир",
    sx: { flex: 1, minWidth: 0 },
    renderCell: (row) => (
      <Stack sx={{ minWidth: 0 }}>
        <CrudLink id={row.id} tab="details" noWrap>
          {row.title}
        </CrudLink>
        <Typography variant="body2" color="textSecondary" noWrap>
          {row.platform?.title ?? "—"}
        </Typography>
      </Stack>
    ),
  },
  stream: {
    title: "Статус",
    sx: { width: 132, flexShrink: 0 },
    renderCell: (row) => renderStatus(row.stream),
  },
});
